import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Paper, 
  TextInput, 
  Select, 
  Group, 
  Button, 
  Text, 
  Badge,
  Stack,
  Divider
} from '@mantine/core';
import { IconSearch, IconFilter, IconRefresh } from '@tabler/icons-react';

export const UserSearchPanel = ({ 
  users,
  onUsersChange, 
  onFilterChange, 
  loading, 
  setLoading 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('signUpDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const searchTimeoutRef = useRef(null);

  // 사용자 정렬
  const sortUsers = (users, sortKey, order) => {
    return [...users].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortKey) {
        case 'signUpDate':
          aValue = new Date(a.profile?.signUpDate || 0);
          bValue = new Date(b.profile?.signUpDate || 0);
          break;
        case 'lastVisit':
          aValue = new Date(a.visitLog?.[a.visitLog.length - 1] || a.profile?.signUpDate || 0);
          bValue = new Date(b.visitLog?.[b.visitLog.length - 1] || b.profile?.signUpDate || 0);
          break;
        case 'visitCount':
          aValue = a.visitLog?.length || 0;
          bValue = b.visitLog?.length || 0;
          break;
        case 'username':
          aValue = a.profile?.username || '';
          bValue = b.profile?.username || '';
          break;
        default:
          aValue = a.profile?.[sortKey] || '';
          bValue = b.profile?.[sortKey] || '';
      }
      
      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  // 필터링 및 검색된 사용자 목록 (useMemo로 최적화)
  const filteredUsers = useMemo(() => {
    let result = [...users];
    
    // 검색어 필터링
    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter(user => {
        const username = user.profile?.username?.toLowerCase() || '';
        const name = user.profile?.name?.toLowerCase() || '';
        const id = user.profile?.id?.toLowerCase() || '';
        
        return username.includes(lowerQuery) || 
               name.includes(lowerQuery) || 
               id.includes(lowerQuery);
      });
    }
    
    // 아바타 필터링
    if (filterType === 'noAvatar') {
      result = result.filter(user => !user.hasAvatar);
    }
    
    // 정렬 적용
    result = sortUsers(result, sortBy, sortOrder);
    
    return result;
  }, [users, searchQuery, filterType, sortBy, sortOrder]);

  // 필터링된 사용자 목록을 부모 컴포넌트에 전달
  useEffect(() => {
    onUsersChange(filteredUsers);
  }, [filteredUsers, onUsersChange]);

  // 검색어 변경 시 디바운스
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      // 검색어 변경 시 자동으로 필터링됨 (useMemo에서 처리)
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // 수동 새로고침 핸들러
  const handleManualRefresh = () => {
    // 현재 필터링된 결과를 다시 적용
    onUsersChange(filteredUsers);
  };

  return (
    <Paper p="md" withBorder>
      <Stack spacing="md">
        <Group position="apart" align="center">
          <Text size="lg" weight={600}>사용자 검색 및 필터</Text>
          <Badge size="lg" variant="filled">
            {filteredUsers.length}명
          </Badge>
        </Group>
        
        <Divider />
        
        <Group grow align="flex-end">
          <TextInput
            placeholder="사용자명, 이름, ID로 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={<IconSearch size={16} />}
            style={{ flex: 1 }}
          />
          
          <Select
            label="필터"
            value={filterType}
            onChange={setFilterType}
            data={[
              { value: 'all', label: '전체 사용자' },
              { value: 'noAvatar', label: '아바타 없는 사용자' }
            ]}
            icon={<IconFilter size={16} />}
            style={{ minWidth: 150 }}
          />
          
          <Select
            label="정렬 기준"
            value={sortBy}
            onChange={setSortBy}
            data={[
              { value: 'signUpDate', label: '가입일' },
              { value: 'lastVisit', label: '최근 방문' },
              { value: 'visitCount', label: '방문 횟수' },
              { value: 'username', label: '사용자명' }
            ]}
            style={{ minWidth: 120 }}
          />
          
          <Select
            label="정렬 순서"
            value={sortOrder}
            onChange={setSortOrder}
            data={[
              { value: 'desc', label: '내림차순' },
              { value: 'asc', label: '오름차순' }
            ]}
            style={{ minWidth: 100 }}
          />
          
          <Button
            onClick={handleManualRefresh}
            loading={loading}
            leftIcon={<IconRefresh size={16} />}
            variant="filled"
          >
            새로고침
          </Button>
        </Group>
        
        {searchQuery && (
          <Text size="sm" color="dimmed">
            "{searchQuery}" 검색 결과: {filteredUsers.length}명
          </Text>
        )}
      </Stack>
    </Paper>
  );
}; 